/* eslint-disable camelcase */
import deduceProviderAccountDetails from '~/components/payment/deposit/data/deduce-provider-details';
import currencyFormatter from '~/libs/currency-formatter';

import { NewQDLogoContainer } from '~/components/payment/quick-deposit-beta/components-v2/styles';

import {
	ADYEN_IDEAL,
	ADYEN_MOBILEPAY,
	CREDITCARD,
	IconMap,
	LogoMap,
	PAYPAL,
	TRUSTLY,
	TRUSTLY_AZURA,
	TRUSTLY_IDEAL
} from '~/components/payment/quick-deposit-beta/utils/constants';

import {
	AccountInfoName,
	IconContainer,
	LogoContainer,
	NewQDIconContainer
} from '~/components/payment/quick-deposit-beta/utils/qd-styled-utils';
import { LogoType } from '~/components/payment-beta/logos/constants';
import PaymentLogos from '~/components/payment-beta/logos';
import styled from 'styled-components';

const StyledContainer = styled.div`
	${({ $isIcon }) =>
		!$isIcon &&
		`
			padding: 0 3px;
			background: #ffffff;
			border-radius: 6px;
			border: 1px solid rgba(232, 234, 236, 1);
			width: 57px;
			height: 31px;
			display: flex;
			justify-content: center;
			align-items: center;
	`}
`;

export const getLogo = (provider, icon, newQDContainer) => {
	const providerNameKey = provider?.paymentMethodNameTextKey;
	let Logo;
	let Container;
	let type = LogoType.colored;

	if (icon) {
		Logo = IconMap[providerNameKey] || IconMap.DEFAULT;
		Container = newQDContainer ? NewQDIconContainer : IconContainer;
		type = LogoType.mono;
	} else {
		Logo = LogoMap[providerNameKey] || LogoMap.DEFAULT;
		Container = newQDContainer ? NewQDLogoContainer : LogoContainer;
	}

	const bankName = provider?.extraParameters?.bankName;

	return bankName ? (
		<StyledContainer $isIcon={icon}>
			<PaymentLogos
				name={bankName}
				type={type}
				color={icon ? 'var(--main-on-primary)' : undefined}
				contrast={icon ? 'var(--main-primary)' : undefined}
				fallback={
					LogoMap[providerNameKey] ? (
						<Container>
							<Logo className={providerNameKey?.toLowerCase()} />
						</Container>
					) : undefined
				}
			/>
		</StyledContainer>
	) : (
		<Container>
			<Logo className={providerNameKey?.toLowerCase()} />
		</Container>
	);
};

export const renderAccountDetails = (provider, component) => {
	const { playerCardData, extraParameters } = provider || {};

	switch (provider?.paymentMethodName) {
		case TRUSTLY_AZURA:
		case ADYEN_IDEAL:
		case TRUSTLY_IDEAL:
		case TRUSTLY: {
			return {
				accountInfo: deduceProviderAccountDetails(extraParameters?.accountNumber),
				selectedBank: extraParameters?.bankName
			};
		}

		case CREDITCARD: {
			return { accountInfo: deduceProviderAccountDetails(playerCardData?.cardNo) };
		}

		case ADYEN_MOBILEPAY: {
			return { accountInfo: deduceProviderAccountDetails(extraParameters?.phoneNumber) };
		}

		case PAYPAL: {
			return {
				accountInfo: component === 'button' ? '' : deduceProviderAccountDetails(extraParameters?.accountNumber)
			};
		}

		default: {
			return {};
		}
	}
};

export const providerLimitInfo = (providerLimitsData, currency, locale) => {
	if (!providerLimitsData) {
		return {};
	}

	const { depositLimitData, paymentProviderData } = providerLimitsData;
	const {
		PARAM_MAX_OPERATION,
		PARAM_MIN_OPERATION,
		PARAM_RESPONSIBLE_GAMING_LIMIT,
		PARAM_RESPONSIBLE_GAMING_MAX_DEPOSIT,
		PARAM_SCHUFA_CONSENT_NEEDED: playerConsentNeeded,
		PARAM_SCHUFA_MONTHLY_REMAINING_AMT: remainingMonthlyLimit,
		responsibleGamingLimitType
	} = depositLimitData || {};
	const { PARAM_PAYMENT_METHOD_ID, PARAM_PAYMENT_PROVIDER_ID } = paymentProviderData || {};
	// Take minimum of 2 values
	const sum_max = Math.floor(Math.min(Number(PARAM_MAX_OPERATION), Number(PARAM_RESPONSIBLE_GAMING_MAX_DEPOSIT)));
	const sum_min = Number(PARAM_MIN_OPERATION);
	const sum_limit = Number(PARAM_RESPONSIBLE_GAMING_LIMIT);
	const paymentMethodId = Number(PARAM_PAYMENT_METHOD_ID);
	const providerId = Number(PARAM_PAYMENT_PROVIDER_ID);
	const schufaLimitHit = remainingMonthlyLimit !== '' && Number(remainingMonthlyLimit) < Number(sum_min);

	const formattedLimit = currencyFormatter.format(sum_limit, currency, locale);
	const formattedSumMin = currencyFormatter.format(sum_min, currency, locale);
	const formattedSumMax = currencyFormatter.format(sum_max, currency, locale);

	const LimitsData = {
		sum_max,
		sum_min,
		sum_limit,
		formattedLimit,
		formattedSumMin,
		formattedSumMax,
		responsibleGamingLimitType,
		playerConsentNeeded,
		remainingMonthlyLimit,
		schufaLimitHit,
		paymentMethodId,
		providerId
	};
	return LimitsData;
};

export const getAccountInfo = provider => {
	const { accountInfo, selectedBank } = renderAccountDetails(provider);
	if (accountInfo || selectedBank) {
		return (
			<>
				{selectedBank ? <AccountInfoName>{selectedBank}&nbsp;</AccountInfoName> : ''}
				{accountInfo || ''}
			</>
		);
	}
	return false;
};

// Define the limit type mapping outside the function so it's only created once
const limitTypeMap = {
	1: 'deposit',
	2: 'spend',
	3: 'balance'
};

export const getLimitType = responsibleGamingLimitType => {
	return limitTypeMap[responsibleGamingLimitType] || 'deposit';
};
